import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { combineQueries } from '@datorama/akita';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { OrderQuery } from '@modules/order/state/order-query';
import { CaseTypeEnum } from '@modules/order/models/case-type.enum';
import { SendToTypeEnum } from '@modules/order/models/send-to-type.enum';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import {
	RX_INTUITIVE_APPLIANCE_DETAILS_VISIBLE$,
	RX_INTUITIVE_DENTURE_DETAILS_VISIBLE$,
	SURGICAL_GUIDE_SECTION_VISIBLE$
} from '@shared/services/sections-visibility.tokens';
import type { Maybe } from '@/types/globals';

@Injectable({
	providedIn: 'root'
})
export class ProcedureSectionVisibilityService {
	isInvisalignDetailsVisible$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(pm => (pm?.ProcedureId as ProcedureEnum) === ProcedureEnum.Invisalign)
	);

	isArtProcedureSelected$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(pm => pm?.ProcedureId === ProcedureEnum.InvisalignART)
	);

	isSendToIDSMessageVisible$: Observable<boolean> = combineQueries([
		this.orderQuery.procedureMap$,
		this.orderQuery.availableProcedureMaps$,
		this.shellQuery.getReadOnlyState('order')
	]).pipe(
		map(([procedureMap, availableProcedureMaps, isReadOnly]) => {
			const isProcedureQualified =
				!!procedureMap &&
				(procedureMap.ProcedureId as ProcedureEnum) === ProcedureEnum.StudyModel_iRecord &&
				(procedureMap.SendToTypes as SendToTypeEnum) !== SendToTypeEnum.chairsideExocad &&
				(procedureMap.SendToTypes as SendToTypeEnum) !== SendToTypeEnum.chairsideGlidewell;

			const hasInvisalignOption =
				availableProcedureMaps?.some(pm => (pm.ProcedureId as ProcedureEnum) === ProcedureEnum.Invisalign) ?? false;

			return isReadOnly ? isProcedureQualified : isProcedureQualified && hasInvisalignOption;
		})
	);

	isScanOptionsVisible$: Observable<boolean> = combineQueries([
		this.shellQuery.isScanOptionsVisible$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(
			([isScanOptionsVisible, procedureMap]) =>
				isScanOptionsVisible ||
				!!(procedureMap?.MultiBiteIsEnabled || procedureMap?.DentureCopyScanIsEnabled || procedureMap?.PreTreatmentScanIsEnabled)
		)
	);

	isTeethDiagramAreaVisible$: Observable<boolean> = combineQueries([
		this.shellQuery.isProcedureFlow$,
		this.orderQuery.caseType$,
		this.orderQuery.procedureMap$
	]).pipe(
		map(([isProcedureFlow, caseTypeSelected, procedureMap]: [boolean, Maybe<{ Id: number }>, Maybe<ProcedureMap>]) => {
			if (isProcedureFlow && procedureMap) {
				return procedureMap.ToothDiagramIsEnabled;
			}

			const caseTypeId = caseTypeSelected?.Id;

			return (
				caseTypeId !== undefined &&
				[
					CaseTypeEnum.Restorative,
					CaseTypeEnum.ChairSideMilling,
					CaseTypeEnum.ChairSideMillingGlidewellIo,
					CaseTypeEnum.ChairSideMillingCerec
				].includes(caseTypeId as CaseTypeEnum)
			);
		})
	);

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		@Inject(RX_INTUITIVE_APPLIANCE_DETAILS_VISIBLE$) public readonly applianceSectionVisible$: Observable<boolean>,
		@Inject(RX_INTUITIVE_DENTURE_DETAILS_VISIBLE$) public readonly dentureDetailsVisible$: Observable<boolean>,
		@Inject(SURGICAL_GUIDE_SECTION_VISIBLE$) public readonly scanSurgicalGuideSectionVisible$: Observable<boolean>
	) {}
}
